import Button from 'devextreme-react/button';
import LoadIndicator from 'devextreme-react/load-indicator';
import DataGrid, {
  Column,
  Item,
  Selection,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import Popup from 'devextreme-react/popup';
import SelectBox, { type SelectBoxRef } from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import CheckBox from 'devextreme-react/check-box';
import { OPS_SITE_GRID_PROPS, OpsGridToolbarItems, OpsSiteGridChrome, useResponsivePopupOptions, OPS_GRID_TOOLBAR_DESKTOP_ONLY, type OpsGridToolbarMenuItem } from '@jarvis/platform-ui';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type {
  CalendarOwnerKind,
  CalendarTarget,
  CalendarTargetSegment
} from '../../../api/timeOff';
import { listCalendarTargets } from '../../../api/timeOff';
import type { DirectoryPrincipal, GraphCalendarCandidate } from '../../../api/calendarTargetsAdmin';
import {
  createCalendarTarget,
  deleteCalendarTarget,
  listGraphCalendars,
  searchDirectoryUsers,
  updateCalendarTarget
} from '../../../api/calendarTargetsAdmin';

type CandidateRow = GraphCalendarCandidate & { _key: string };

const OWNER_KIND_ITEMS: ReadonlyArray<{ value: CalendarOwnerKind; text: string }> = [
  { value: 'UserMailbox', text: 'Mailbox (user or shared resource)' }
];
const SEGMENT_ITEMS: ReadonlyArray<{ value: CalendarTargetSegment; text: string }> = [
  { value: 'Company', text: 'Company HR (OpsHRCompany)' },
  { value: 'Contract', text: 'Contract HR (OpsHRContract)' }
];

/** Reduce password-manager overlays on non-credential fields (hints only; not foolproof). */
const NO_AUTOFILL_INPUT_ATTR = {
  autocomplete: 'off',
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-bwignore': 'true'
} as Record<string, string>;

const fieldLabel: CSSProperties = {
  fontWeight: 600,
  marginBottom: 6,
  fontSize: 13,
  display: 'block',
  color: 'var(--ops-color-text, #111827)'
};

function principalLabel(p: DirectoryPrincipal | null | undefined): string {
  if (!p) return '';
  const secondary = p.mail ?? p.userPrincipalName ?? '';
  return secondary ? `${p.displayName} (${secondary})` : p.displayName;
}

function candidateKey(c: GraphCalendarCandidate): string {
  return `${c.ownerId}|${c.graphCalendarId ?? '__default__'}`;
}

const CANDIDATES_GRID_KEY = 'webopsui:calendar-target-candidates-grid:v2';
const REGISTERED_GRID_KEY = 'webopsui:calendar-targets-grid:v2';
const CALENDAR_CANDIDATES_GRID_COLUMNS = [
  'name',
  'graphCalendarId',
  'isDefaultCalendar',
  'ownerKind',
  'ownerId'
] as const;
const CALENDAR_REGISTERED_GRID_COLUMNS = [
  'displayName',
  'segment',
  'graphCalendarOwnerKind',
  'graphOwnerId',
  'graphCalendarId',
  'isEnabled',
  'syncDirection'
] as const;

export function CalendarTargetsAdminPage() {
  const candidatesGridRef = useRef<DataGridRef>(null);
  const registeredGridRef = useRef<DataGridRef>(null);
  const [anchorKind, setAnchorKind] = useState<CalendarOwnerKind>('UserMailbox');
  const [manualOwnerId, setManualOwnerId] = useState('');
  const [selectedPrincipal, setSelectedPrincipal] = useState<DirectoryPrincipal | null>(null);
  const [directoryQuery, setDirectoryQuery] = useState('');
  const [directoryItems, setDirectoryItems] = useState<DirectoryPrincipal[]>([]);
  const [loadingDirectory, setLoadingDirectory] = useState(false);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [directorySearchCompletedFor, setDirectorySearchCompletedFor] = useState<string | null>(null);
  const directorySelectRef = useRef<SelectBoxRef>(null);

  const [candidates, setCandidates] = useState<CandidateRow[]>([]);
  const [selectedCandidateKeys, setSelectedCandidateKeys] = useState<string[]>([]);
  const [selectedCandidates, setSelectedCandidates] = useState<GraphCalendarCandidate[]>([]);
  const [registerNames, setRegisterNames] = useState<Record<string, string>>({});
  const [targetSegment, setTargetSegment] = useState<CalendarTargetSegment>('Company');
  const [registerInboundSync, setRegisterInboundSync] = useState(false);

  const [registered, setRegistered] = useState<CalendarTarget[]>([]);
  const [loadingRegistered, setLoadingRegistered] = useState(false);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registering, setRegistering] = useState(false);

  const [editTarget, setEditTarget] = useState<CalendarTarget | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editSegment, setEditSegment] = useState<CalendarTargetSegment>('Company');
  const [editEnabled, setEditEnabled] = useState(true);
  const [editInboundSync, setEditInboundSync] = useState(false);
  const [editClearInboundAnchor, setEditClearInboundAnchor] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const registerPopupOptions = useResponsivePopupOptions({ width: 560, height: 'auto' });
  const editPopupOptions = useResponsivePopupOptions({ width: 420, height: 'auto' });

  const [pageError, setPageError] = useState<string | null>(null);
  const [sectionInfo, setSectionInfo] = useState<string | null>(null);
  /** Last owner key (`anchorKind:id`) for which a calendar load finished (success or fail). */
  const [completedCalendarLoadKey, setCompletedCalendarLoadKey] = useState<string | null>(null);

  const effectiveOwnerId = manualOwnerId.trim() || selectedPrincipal?.id || '';
  const ownerLoadKey = effectiveOwnerId ? `${anchorKind}:${effectiveOwnerId}` : '';

  useEffect(() => {
    setCompletedCalendarLoadKey(null);
    setCandidates([]);
    setSelectedCandidateKeys([]);
    setSelectedCandidates([]);
  }, [ownerLoadKey]);

  const syncCandidateSelection = useCallback(
    (keys: string[], rows?: CandidateRow[]) => {
      const normalized = keys.map(String);
      setSelectedCandidateKeys(normalized);
      const source = rows ?? candidates;
      setSelectedCandidates(source.filter((row) => normalized.includes(row._key)));
    },
    [candidates]
  );

  useEffect(() => {
    const q = directoryQuery.trim();
    if (q.length < 2) {
      setDirectoryItems([]);
      setDirectoryError(null);
      setLoadingDirectory(false);
      setDirectorySearchCompletedFor(null);
      return;
    }

    setDirectorySearchCompletedFor(null);
    const handle = window.setTimeout(() => {
      void (async () => {
        setLoadingDirectory(true);
        setDirectoryError(null);
        try {
          const data = await searchDirectoryUsers(q);
          setDirectoryItems(data);
          setDirectorySearchCompletedFor(q);
        } catch (err) {
          setDirectoryItems([]);
          setDirectorySearchCompletedFor(q);
          setDirectoryError(err instanceof Error ? err.message : 'Directory search failed.');
        } finally {
          setLoadingDirectory(false);
        }
      })();
    }, 400);
    return () => window.clearTimeout(handle);
  }, [directoryQuery, anchorKind]);

  const trimmedDirectoryQuery = directoryQuery.trim();
  useEffect(() => {
    if (
      trimmedDirectoryQuery.length < 2 ||
      loadingDirectory ||
      directorySearchCompletedFor !== trimmedDirectoryQuery ||
      directoryItems.length === 0
    ) {
      return;
    }
    directorySelectRef.current?.instance()?.open();
  }, [directoryItems.length, directorySearchCompletedFor, loadingDirectory, trimmedDirectoryQuery]);

  const refreshRegistered = useCallback(async () => {
    setLoadingRegistered(true);
    setPageError(null);
    try {
      setRegistered(await listCalendarTargets());
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to load calendar targets.');
    } finally {
      setLoadingRegistered(false);
    }
  }, []);

  useEffect(() => {
    void refreshRegistered();
  }, [refreshRegistered]);

  const loadCalendarCandidates = useCallback(
    async (opts?: { silent?: boolean }) => {
      const ownerId = manualOwnerId.trim() || selectedPrincipal?.id || '';
      if (!ownerId) {
        if (!opts?.silent) {
          setPageError('Choose an owner from directory search or enter an Object ID / UPN under Advanced.');
        }
        return;
      }
      setLoadingCalendars(true);
      if (!opts?.silent) {
        setPageError(null);
        setSectionInfo(null);
      }
      try {
        let rows: GraphCalendarCandidate[] = [];

        let info: string;
        if (rows.length === 0) {
          rows = await listGraphCalendars(anchorKind, ownerId);
          info =
            rows.length === 0
              ? 'Microsoft Graph returned no calendars for this owner.'
              : 'Loaded live from Microsoft Graph.';
        } else {
          info = `Loaded ${rows.length} calendar(s) from cached catalog. Use Refresh catalog (server job) to refresh all groups.`;
        }

        setCandidates(rows.map((r) => ({ ...r, _key: candidateKey(r) })));
        setSelectedCandidateKeys([]);
        setSelectedCandidates([]);
        if (!opts?.silent || rows.length > 0) setSectionInfo(info);
        const okId = manualOwnerId.trim() || selectedPrincipal?.id || '';
        if (okId) setCompletedCalendarLoadKey(`${anchorKind}:${okId}`);
      } catch (err) {
        setCandidates([]);
        setSelectedCandidateKeys([]);
        setSelectedCandidates([]);
        setPageError(err instanceof Error ? err.message : 'Failed to load calendars.');
        const failId = manualOwnerId.trim() || selectedPrincipal?.id || '';
        if (failId) setCompletedCalendarLoadKey(`${anchorKind}:${failId}`);
      } finally {
        setLoadingCalendars(false);
      }
    },
    [anchorKind, manualOwnerId, selectedPrincipal?.id]
  );

  async function loadCalendarCandidatesExplicit() {
    await loadCalendarCandidates({ silent: false });
  }

  useEffect(() => {
    if (manualOwnerId.trim()) return;
    if (!selectedPrincipal?.id) return;
    void loadCalendarCandidates({ silent: true });
  }, [selectedPrincipal?.id, manualOwnerId, loadCalendarCandidates]);

  useEffect(() => {
    const manual = manualOwnerId.trim();
    if (!manual) return;
    const handle = window.setTimeout(() => {
      void loadCalendarCandidates({ silent: false });
    }, 600);
    return () => window.clearTimeout(handle);
  }, [manualOwnerId, loadCalendarCandidates]);

  function openRegister() {
    setPageError(null);
    setRegisterInboundSync(false);
    if (selectedCandidateKeys.length === 0) {
      setPageError('Select one or more calendars in the grid first (use the checkboxes or click a row).');
      return;
    }
    const names: Record<string, string> = {};
    for (const c of selectedCandidates) names[candidateKey(c)] = c.name;
    setRegisterNames(names);
    setRegisterOpen(true);
  }

  const candidatesToolbarMenuItems = useMemo<OpsGridToolbarMenuItem[]>(
    () => [
      {
        id: 'register-selected',
        text: 'Register selected…',
        icon: 'add',
        disabled: selectedCandidateKeys.length === 0,
        onClick: openRegister
      }
    ],
    [selectedCandidateKeys.length, selectedCandidates]
  );

  async function submitRegister() {
    const batch = [...selectedCandidates];
    setRegistering(true);
    setPageError(null);
    try {
      for (const c of batch) {
        const key = candidateKey(c);
        const displayName = (registerNames[key] ?? c.name).trim();
        if (!displayName) throw new Error('Display name is required for every row.');
        await createCalendarTarget({
          displayName,
          segment: targetSegment,
          graphCalendarOwnerKind: c.ownerKind,
          graphOwnerId: c.ownerId,
          graphCalendarId: c.graphCalendarId,
          isEnabled: true,
          syncDirection: registerInboundSync ? 'Bidirectional' : 'PushOnly'
        });
      }
      setRegisterOpen(false);
      setSelectedCandidateKeys([]);
      setSelectedCandidates([]);
      setSectionInfo(`Registered ${batch.length} calendar target(s).`);
      await refreshRegistered();
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Registration failed.');
    } finally {
      setRegistering(false);
    }
  }

  function openEdit(row: CalendarTarget) {
    setEditTarget(row);
    setEditDisplayName(row.displayName);
    setEditSegment(row.segment);
    setEditEnabled(row.isEnabled);
    setEditInboundSync(row.syncDirection === 'Bidirectional');
    setEditClearInboundAnchor(false);
    setEditSaving(false);
  }

  async function saveEdit() {
    if (!editTarget) return;
    const name = editDisplayName.trim();
    if (!name) {
      setPageError('Display name is required.');
      return;
    }
    setEditSaving(true);
    setPageError(null);
    try {
      await updateCalendarTarget(editTarget.id, {
        displayName: name,
        segment: editSegment,
        isEnabled: editEnabled,
        syncDirection: editInboundSync ? 'Bidirectional' : 'PushOnly',
        clearInboundCalendarEventsDeltaLink: editClearInboundAnchor ? true : undefined
      });
      setEditTarget(null);
      await refreshRegistered();
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setEditSaving(false);
    }
  }

  async function removeTarget(row: CalendarTarget) {
    if (
      !window.confirm(
        `Remove calendar target "${row.displayName}"? Entries that exist only on this calendar will be deleted. Entries on other calendars are kept. Outlook events are not removed automatically.`
      )
    ) {
      return;
    }
    setPageError(null);
    try {
      await deleteCalendarTarget(row.id);
      await refreshRegistered();
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Delete failed.');
    }
  }

  return (
    <div className="ops-page">
        {pageError ? (
          <div style={{ color: 'var(--ops-color-danger, #b91c1c)', fontWeight: 600 }}>{pageError}</div>
        ) : null}
        {sectionInfo ? (
          <div style={{ color: 'var(--ops-color-muted, #475569)', fontSize: 14 }}>{sectionInfo}</div>
        ) : null}

        <section className="ops-section">
          <h3 className="ops-section__title" style={{ marginBottom: 4 }}>Discover from Graph</h3>
          <p
            style={{
              margin: '0 0 8px',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--ops-color-text, #334155)'
            }}
          >
            Choose owner → Load calendars → pick rows to register
          </p>
          <ol
            style={{
              margin: '0 0 12px',
              paddingLeft: 18,
              fontSize: 13,
              color: 'var(--ops-color-muted, #64748b)',
              lineHeight: 1.45
            }}
          >
            <li>Search directory and pick a <strong>mailbox owner</strong> (or use Advanced with object id / UPN).</li>
            <li>
              Calendars for that mailbox load automatically (or click Load calendars). App-only Graph permissions support user and shared mailbox calendars.
            </li>
            <li>
              This grid lists <strong>calendars</strong>, not directory hits. Select rows, choose HR segment, then register.
            </li>
          </ol>

          <div className="calendar-targets-discover-form">
            <div className="calendar-targets-discover-form__owner">
              <label style={fieldLabel}>Owner type</label>
              <SelectBox
                items={[...OWNER_KIND_ITEMS]}
                valueExpr="value"
                displayExpr="text"
                value={anchorKind}
                width="100%"
                inputAttr={NO_AUTOFILL_INPUT_ATTR}
                onValueChanged={(e) => {
                  setAnchorKind(e.value as CalendarOwnerKind);
                  setSelectedPrincipal(null);
                  setDirectoryQuery('');
                  setDirectoryItems([]);
                  setDirectoryError(null);
                }}
              />
              <label style={{ ...fieldLabel, marginTop: 10 }}>HR segment (new registrations)</label>
              <SelectBox
                items={[...SEGMENT_ITEMS]}
                valueExpr="value"
                displayExpr="text"
                value={targetSegment}
                width="100%"
                inputAttr={NO_AUTOFILL_INPUT_ATTR}
                onValueChanged={(e) => setTargetSegment(e.value as CalendarTargetSegment)}
              />
            </div>

            <div className="calendar-targets-discover-form__search">
              <label style={fieldLabel}>Directory search</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <TextBox
                  value={directoryQuery}
                  valueChangeEvent="input"
                  onValueChanged={(e) => setDirectoryQuery(String(e.value ?? ''))}
                  placeholder="Type at least 2 characters — search runs automatically"
                  width="100%"
                  inputAttr={NO_AUTOFILL_INPUT_ATTR}
                />
                {loadingDirectory ? <LoadIndicator height={26} width={26} visible /> : <span style={{ width: 26, flexShrink: 0 }} aria-hidden />}
              </div>
              {trimmedDirectoryQuery.length < 2 ? (
                <div style={{ fontSize: 12, color: 'var(--ops-color-muted, #6b7280)', marginTop: 6, lineHeight: 1.35 }}>
                  Search by display name or UPN. Results appear below as you type.
                </div>
              ) : loadingDirectory ? (
                <div style={{ fontSize: 12, color: 'var(--ops-color-muted, #6b7280)', marginTop: 6, lineHeight: 1.35 }}>
                  Searching directory…
                </div>
              ) : directoryError ? null : directorySearchCompletedFor === trimmedDirectoryQuery ? (
                <div style={{ fontSize: 12, color: 'var(--ops-color-muted, #6b7280)', marginTop: 6, lineHeight: 1.35 }}>
                  {directoryItems.length === 0
                    ? `No mailboxes matched "${trimmedDirectoryQuery}". Try different keywords or use Advanced.`
                    : `${directoryItems.length} mailbox${directoryItems.length === 1 ? '' : 'es'} found — pick one below.`}
                </div>
              ) : null}
              {directoryError ? (
                <div style={{ fontSize: 12, color: 'var(--ops-color-danger, #b91c1c)', marginTop: 6 }}>{directoryError}</div>
              ) : null}
            </div>

            <div className="calendar-targets-discover-form__load">
              <Button
                text={loadingCalendars ? 'Loading…' : 'Load calendars'}
                icon="search"
                type="default"
                stylingMode="contained"
                onClick={() => void loadCalendarCandidatesExplicit()}
                disabled={loadingCalendars || !effectiveOwnerId}
              />
              {loadingCalendars ? <LoadIndicator height={28} width={28} visible /> : null}
            </div>

            <div className="calendar-targets-discover-form__mailbox">
              <label style={fieldLabel}>Pick mailbox owner</label>
              <SelectBox
                ref={directorySelectRef}
                key={anchorKind}
                items={directoryItems}
                displayExpr={principalLabel}
                valueExpr="id"
                searchEnabled={false}
                showClearButton
                openOnFieldClick
                width="100%"
                inputAttr={NO_AUTOFILL_INPUT_ATTR}
                placeholder={
                  trimmedDirectoryQuery.length < 2
                    ? 'Enter a search above first…'
                    : loadingDirectory
                      ? 'Searching…'
                      : directoryItems.length === 0
                        ? 'No matches — try different keywords'
                        : 'Select mailbox owner…'
                }
                disabled={trimmedDirectoryQuery.length < 2}
                noDataText={
                  directorySearchCompletedFor === trimmedDirectoryQuery
                    ? `No mailboxes matched "${trimmedDirectoryQuery}"`
                    : 'Searching…'
                }
                value={manualOwnerId.trim() ? null : (selectedPrincipal?.id ?? null)}
                onValueChanged={(e) => {
                  const id = e.value as string | null | undefined;
                  if (!id) {
                    setSelectedPrincipal(null);
                    return;
                  }
                  const item = directoryItems.find((p) => p.id === id);
                  setSelectedPrincipal(item ?? null);
                }}
              />
              <div style={{ fontSize: 12, color: 'var(--ops-color-muted, #6b7280)', marginTop: 6, lineHeight: 1.35 }}>
                This dropdown is the <strong>mailbox</strong>, not a calendar. After you pick one, calendars load in the grid below.
                Paste a mailbox UPN or object ID under <span style={{ fontWeight: 600 }}>Advanced</span> if you already know it.
              </div>
              {selectedPrincipal && !manualOwnerId.trim() ? (
                <div style={{ fontSize: 12, color: 'var(--ops-color-text, #334155)', marginTop: 6, lineHeight: 1.35 }}>
                  Owner selected: <strong>{principalLabel(selectedPrincipal)}</strong>
                  {loadingCalendars ? ' — loading calendars…' : candidates.length > 0 ? ` — ${candidates.length} calendar(s) ready.` : null}
                </div>
              ) : null}
              {directoryError ? (
                <div role="alert" style={{ color: 'var(--ops-color-danger, #b91c1c)', fontSize: 13, marginTop: 8 }}>
                  {directoryError}
                </div>
              ) : null}
            </div>
          </div>

          <div style={{ marginBottom: 12, maxWidth: 720 }}>
            <label style={fieldLabel}>Advanced — manual owner id (overrides directory selection)</label>
            <TextBox
              value={manualOwnerId}
              onValueChanged={(e) => setManualOwnerId(String(e.value ?? ''))}
              placeholder="Object ID or UPN (optional)"
              width="100%"
              inputAttr={NO_AUTOFILL_INPUT_ATTR}
            />
          </div>

          {!loadingCalendars && candidates.length === 0 ? (
            <p
              style={{
                margin: '0 0 8px',
                fontSize: 13,
                color: 'var(--ops-color-muted, #64748b)',
                fontStyle: !effectiveOwnerId ? 'italic' : 'normal'
              }}
              role="status"
            >
              {!effectiveOwnerId
                ? 'Pick or enter a mailbox owner first. Calendar rows appear in the grid after load.'
                : loadingCalendars
                  ? 'Loading calendars from Microsoft Graph…'
                  : completedCalendarLoadKey !== ownerLoadKey
                    ? 'Waiting to load calendars for this owner…'
                    : 'Load finished: no calendars returned for this owner. Try Load calendars again or verify the object ID / UPN.'}
            </p>
          ) : null}

          <div style={{ position: 'relative', minHeight: loadingCalendars && candidates.length === 0 ? 120 : undefined }}>
            {loadingCalendars && candidates.length === 0 ? (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.65)',
                  zIndex: 2,
                  borderRadius: 4
                }}
              >
                <LoadIndicator height={40} width={40} visible />
              </div>
            ) : null}
          <DataGrid
            ref={candidatesGridRef}
            key={ownerLoadKey || 'no-owner'}
            dataSource={candidates}
            keyExpr="_key"
            showBorders
            columnAutoWidth
            columnHidingEnabled
            allowColumnResizing
            allowColumnReordering
            columnResizingMode="widget"
            {...OPS_SITE_GRID_PROPS}
            onSelectionChanged={(e) => {
              syncCandidateSelection((e.selectedRowKeys ?? []) as string[], (e.selectedRowsData ?? []) as CandidateRow[]);
            }}
          >
            <OpsSiteGridChrome
              stateStorageKey={CANDIDATES_GRID_KEY}
              stateColumns={CALENDAR_CANDIDATES_GRID_COLUMNS}
              searchPlaceholder="Search calendars…"
              showPaging={false}
            />
            <Toolbar>
              <Item location="before" cssClass={OPS_GRID_TOOLBAR_DESKTOP_ONLY}>
                <Button
                  text="Register selected…"
                  icon="add"
                  type="default"
                  stylingMode="contained"
                  disabled={selectedCandidateKeys.length === 0}
                  onClick={() => openRegister()}
                />
              </Item>
              <OpsGridToolbarItems
                gridRef={candidatesGridRef}
                loading={loadingCalendars}
                onRefresh={() => void loadCalendarCandidates()}
                refreshHint="Reload calendars"
                extraMenuItems={candidatesToolbarMenuItems}
              />
              <Item name="searchPanel" location="after" />
            </Toolbar>
            <Selection mode="multiple" showCheckBoxesMode="always" selectAllMode="allPages" />
            <Column dataField="name" caption="Calendar name" />
            <Column dataField="graphCalendarId" caption="Graph calendar id" />
            <Column dataField="isDefaultCalendar" caption="Default" dataType="boolean" />
            <Column dataField="ownerKind" caption="Owner kind" width={130} />
            <Column dataField="ownerId" caption="Owner id" />
          </DataGrid>
          </div>
          <div className={OPS_GRID_TOOLBAR_DESKTOP_ONLY} style={{ marginTop: '0.5rem' }}>
            <Button
              text="Register selected…"
              icon="add"
              type="default"
              stylingMode="contained"
              disabled={selectedCandidateKeys.length === 0}
              onClick={() => openRegister()}
            />
          </div>
        </section>

        <section className="ops-section">
          <div className="ops-section__header">
            <h3 className="ops-section__title">Registered targets</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {loadingRegistered ? <LoadIndicator height={24} width={24} visible /> : null}
            </div>
          </div>
          {!loadingRegistered && registered.length === 0 ? (
            <p style={{ margin: '0.5rem 0', color: 'var(--ops-color-muted, #64748b)', fontSize: 14 }}>
              No calendar targets yet — register some from Graph above, then use Refresh list if needed.
            </p>
          ) : null}
          <p style={{ margin: '0.35rem 0 0.6rem', color: 'var(--ops-color-muted, #64748b)', fontSize: 13 }}>
            Double‑click a row or use Edit to update display name, segment, inbound sync, or delta anchor clearing. Remove deletes entries that only existed on that calendar, then removes the target.
          </p>
          <DataGrid
            ref={registeredGridRef}
            dataSource={registered}
            keyExpr="id"
            showBorders
            columnAutoWidth
            columnHidingEnabled
            allowColumnResizing
            allowColumnReordering
            columnResizingMode="widget"
            height="calc(100vh - 240px)"
            {...OPS_SITE_GRID_PROPS}
            onRowDblClick={(e) => {
              if (e.data) openEdit(e.data as CalendarTarget);
            }}
          >
            <OpsSiteGridChrome
              stateStorageKey={REGISTERED_GRID_KEY}
              stateColumns={CALENDAR_REGISTERED_GRID_COLUMNS}
              searchPlaceholder="Search targets…"
              showPaging={false}
            />
            <Toolbar>
              <OpsGridToolbarItems
                gridRef={registeredGridRef}
                loading={loadingRegistered}
                onRefresh={() => void refreshRegistered()}
                refreshHint="Refresh targets list"
              />
              <Item name="searchPanel" location="after" />
            </Toolbar>

            <Column dataField="displayName" caption="Display name" />
            <Column dataField="segment" caption="HR segment" width={110} />
            <Column dataField="graphCalendarOwnerKind" caption="Kind" width={130} />
            <Column dataField="graphOwnerId" caption="Owner id" />
            <Column dataField="graphCalendarId" caption="Calendar id" />
            <Column dataField="isEnabled" caption="Enabled" dataType="boolean" />
            <Column dataField="syncDirection" caption="Sync direction" />
            <Column
              caption="Delta anchor"
              width={110}
              dataType="boolean"
              allowFiltering={false}
              calculateCellValue={(row: CalendarTarget) => row.hasInboundCalendarEventsDeltaAnchor}
            />
            <Column
              caption="Actions"
              width={190}
              allowFiltering={false}
              allowSorting={false}
              cellRender={(cell) => {
                const row = cell.data as CalendarTarget;
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <Button
                      stylingMode="text"
                      type="default"
                      icon="edit"
                      text="Edit"
                      onClick={() => openEdit(row)}
                    />
                    <Button
                      stylingMode="text"
                      type="danger"
                      icon="remove"
                      text="Remove"
                      onClick={() => void removeTarget(row)}
                    />
                  </div>
                );
              }}
            />
          </DataGrid>
        </section>

        <Popup
          visible={registerOpen}
          onHiding={() => !registering && setRegisterOpen(false)}
          title="Register calendar targets"
          showCloseButton
          dragEnabled
          {...registerPopupOptions}
        >
          <div style={{ padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <p style={{ margin: 0, color: 'var(--ops-color-muted, #6b7280)', fontSize: 13 }}>
              Adjust display names as needed. Display name must be unique per HR segment (Company vs Contract).
            </p>
            <p style={{ margin: 0, color: 'var(--ops-status-partial-fg, var(--brand-accent))', fontSize: 13 }}>
              Dedicated time-off calendars only: inbound sync assumes every calendar event is a candidate time-off record. Prefer a mailbox calendar that is empty or used exclusively for HR time off.
            </p>
            <CheckBox
              text="Inbound sync into WebOps (bidirectional)"
              value={registerInboundSync}
              onValueChanged={(e) => setRegisterInboundSync(!!e.value)}
            />
            {selectedCandidates.map((c) => {
              const key = candidateKey(c);
              return (
                <div key={key} style={{ borderBottom: '1px solid var(--ops-border, #e5e7eb)', paddingBottom: '0.5rem' }}>
                  <div style={{ fontSize: 12, color: 'var(--ops-color-muted, #6b7280)' }}>
                    {c.ownerKind} · {c.name}
                  </div>
                  <TextBox
                    label="Display name"
                    labelMode="outside"
                    value={registerNames[key] ?? ''}
                    inputAttr={NO_AUTOFILL_INPUT_ATTR}
                    onValueChanged={(e) =>
                      setRegisterNames((prev) => ({ ...prev, [key]: String(e.value ?? '') }))
                    }
                  />
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <Button text="Cancel" onClick={() => !registering && setRegisterOpen(false)} disabled={registering} />
              <Button
                text={registering ? 'Saving…' : 'Register'}
                type="default"
                stylingMode="contained"
                disabled={registering}
                onClick={() => void submitRegister()}
              />
            </div>
          </div>
        </Popup>

        <Popup
          visible={editTarget !== null}
          onHiding={() => !editSaving && setEditTarget(null)}
          title="Edit calendar target"
          showCloseButton
          dragEnabled
          {...editPopupOptions}
        >
          <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <TextBox
              label="Display name"
              labelMode="outside"
              value={editDisplayName}
              inputAttr={NO_AUTOFILL_INPUT_ATTR}
              onValueChanged={(e) => setEditDisplayName(String(e.value ?? ''))}
            />
            <SelectBox
              label="HR segment"
              labelMode="outside"
              items={[...SEGMENT_ITEMS]}
              valueExpr="value"
              displayExpr="text"
              value={editSegment}
              inputAttr={NO_AUTOFILL_INPUT_ATTR}
              onValueChanged={(e) => setEditSegment(e.value as CalendarTargetSegment)}
            />
            <CheckBox text="Enabled (shown to Ops HR)" value={editEnabled} onValueChanged={(e) => setEditEnabled(!!e.value)} />
            <p style={{ margin: 0, color: 'var(--ops-status-partial-fg, var(--brand-accent))', fontSize: 13 }}>
              Enabling inbound is for dedicated time-off calendars. WebOps derives the employee from required attendees — not from the organizer.
            </p>
            <CheckBox
              text="Inbound sync into WebOps (bidirectional)"
              value={editInboundSync}
              onValueChanged={(e) => setEditInboundSync(!!e.value)}
            />
            <CheckBox
              text="Clear stored Graph delta anchor (forces full ingest on next bidirectional sync)"
              value={editClearInboundAnchor}
              disabled={!editTarget?.hasInboundCalendarEventsDeltaAnchor}
              onValueChanged={(e) => setEditClearInboundAnchor(!!e.value)}
            />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <Button text="Cancel" onClick={() => !editSaving && setEditTarget(null)} disabled={editSaving} />
              <Button
                text={editSaving ? 'Saving…' : 'Save'}
                type="default"
                stylingMode="contained"
                disabled={editSaving}
                onClick={() => void saveEdit()}
              />
            </div>
          </div>
        </Popup>
    </div>
  );
}
